import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import SearchableSelect from '../shared/SearchableSelect';
import SearchableMultiSelect from '../shared/SearchableMultiSelect';
import { currentPublishTargets, targetStoreOptions } from '../../utils/publishTargets';
import CanvasExecutionManager, { MODELS, EFFORTS, COMPUTE_WARNING, effortLabel, summarizeOverrides } from './CanvasExecutionManager';

// A template may PIN how its blocks compute, so a dashboard that genuinely
// needs a stronger model arrives configured rather than depending on whatever
// each consuming account happens to have set. Left on Inherit — the default —
// the template pins nothing.
const INHERIT = '__inherit__';

// Source-account scope. '' is "nothing chosen yet"; ALL_ACCOUNTS lists canvases
// across every account, for an admin who knows the canvas but not which account
// it lives in.
const ALL_ACCOUNTS = '__all__';

const DELIVERIES = ['auto', 'file', 'inline'];

// Admin-only cross-account Canvas Template publisher. Browse ANY account's
// canvases and publish one to all accounts or specific ones, without switching
// the current account. Modelled on PublishManagerModal (Prebuilt Agents); the
// endpoints are the canvas twins under /api/cognitive-flows/admin/canvases*.
//
// Picking an already-published canvas pre-fills its targets, description and
// pins: the publish route REPLACES all three, so an empty form would silently
// un-share it and wipe them.

const s = {
  wrap: { background: 'var(--surface,#fff)', borderRadius: '14px', width: '100%', maxWidth: '760px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--border)' },
  title: { fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' },
  body: { padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '20px' },
  sectionTitle: { fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px' },
  label: { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '5px', display: 'block' },
  select: { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', marginBottom: '14px' },
  // Trigger look for the searchable pickers (the native <select> style, minus the margin).
  trigger: { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff' },
  field: { marginBottom: '14px' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', marginBottom: '14px', boxSizing: 'border-box' },
  radioRow: { display: 'flex', gap: '18px', marginBottom: '12px', fontSize: '13px' },
  btnPrimary: (disabled) => ({ padding: '8px 18px', borderRadius: '8px', border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: '13px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }),
  card: { border: '1px solid var(--border)', borderRadius: '10px', padding: '20px 22px', background: '#fbfcfe' },
  pubRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', padding: '12px 0', borderBottom: '1px solid var(--border)' },
  pubName: { fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' },
  pubMeta: { fontSize: '11px', color: 'var(--text-muted)' },
  targetLabel: { fontSize: '11.5px', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '7px' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '5px' },
  chip: { display: 'inline-block', padding: '2px 9px', borderRadius: '20px', background: '#eef2ff', color: '#3730a3', fontSize: '11px', fontWeight: 600 },
  chipAll: { display: 'inline-block', padding: '2px 9px', borderRadius: '20px', background: '#dcfce7', color: '#166534', fontSize: '11px', fontWeight: 700 },
  moreBtn: { padding: '2px 9px', borderRadius: '20px', border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 600, cursor: 'pointer' },
  pinList: { margin: '6px 0 0', paddingLeft: '15px', fontSize: '11.5px', color: 'var(--text-secondary)', lineHeight: 1.6 },
  pubDesc: { fontSize: '12px', color: 'var(--text-secondary)', marginTop: '5px', lineHeight: 1.5 },
  unpubBtn: { padding: '5px 12px', borderRadius: '8px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: '12px', cursor: 'pointer', flexShrink: 0 },
  empty: { fontSize: '13px', color: 'var(--text-muted)', padding: '8px 0' },
};

// How many target accounts to show before collapsing. A template published to
// forty accounts must not push the Unpublish button off the panel, but the
// point of this row is that the targets are VISIBLE, so it collapses rather
// than truncating silently.
const TARGET_CHIP_LIMIT = 8;

const publishedOn = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

function PublishedRow({ row, nameOf, onUnpublish }) {
  const [showAll, setShowAll] = useState(false);
  const targets = row.publishShops || [];
  const toAll = row.publishVisibility === 'all';
  const shown = showAll ? targets : targets.slice(0, TARGET_CHIP_LIMIT);
  const pins = summarizeOverrides(row.execOverrides || {});
  const when = publishedOn(row.publishedAt);

  return (
    <div style={s.pubRow}>
      <div style={{ minWidth: 0 }}>
        <div style={s.pubName}>{row.name}</div>
        <div style={s.pubMeta}>
          from {nameOf(row.shop)}
          {when && ` · published ${when}`}
        </div>
        {/* The blurb end users see on the library card — the one place to
            check it without re-opening the canvas in the publish form. */}
        {row.templateDescription && <div style={s.pubDesc}>{row.templateDescription}</div>}

        <div style={s.targetLabel}>
          Published to{!toAll && targets.length > 0 && ` (${targets.length})`}
        </div>
        <div style={s.chipWrap}>
          {toAll && <span style={s.chipAll}>All accounts</span>}
          {/* Visibility 'stores' with nothing selected is refused at publish
              time, so an empty list here means a legacy or hand-edited doc.
              Say so — a silently blank row reads as a loading bug. */}
          {!toAll && targets.length === 0 && <span style={s.pubMeta}>No accounts selected</span>}
          {!toAll && shown.map((key) => (
            // nameOf falls back to the raw key, which is what an account
            // removed since publishing looks like.
            <span key={key} style={s.chip}>{nameOf(key)}</span>
          ))}
          {!toAll && targets.length > TARGET_CHIP_LIMIT && (
            <button type="button" style={s.moreBtn} onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show fewer' : `+${targets.length - TARGET_CHIP_LIMIT} more`}
            </button>
          )}
        </div>

        {pins.length > 0 && (
          <>
            <div style={s.targetLabel}>Pinned execution options</div>
            <ul style={s.pinList}>
              {pins.map((line) => <li key={line}>{line}</li>)}
            </ul>
          </>
        )}
      </div>
      <button type="button" className="btn-height" style={s.unpubBtn} onClick={() => onUnpublish(row._id)}>Unpublish</button>
    </div>
  );
}

export default function CanvasPublishManager() {
  const [stores, setStores] = useState([]);
  const [sourceShop, setSourceShop] = useState('');
  const [canvases, setCanvases] = useState([]);
  const [loadingCv, setLoadingCv] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [targetShops, setTargetShops] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState([]);
  // What this template pins. INHERIT = pin nothing for that key.
  const [execModel, setExecModel] = useState(INHERIT);
  const [execEffort, setExecEffort] = useState(INHERIT);
  const [execDelivery, setExecDelivery] = useState(INHERIT);

  const execOverridesPayload = () => {
    const out = {};
    if (execModel !== INHERIT) {
      const [provider, model] = execModel.split('|');
      out.provider = provider;
      out.model = model;
    }
    if (execEffort !== INHERIT) out.reasoningEffort = execEffort;
    if (execDelivery !== INHERIT) out.dataDelivery = execDelivery;
    return out;
  };

  const nameOf = useCallback((key) => stores.find((st) => st.shopKey === key)?.name || key, [stores]);

  const loadPublished = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/canvases/published');
      setPublished(data);
    } catch {
      // non-fatal; the panel still lets you publish
    }
  }, []);

  useEffect(() => {
    api.get('/admin/stores').then(({ data }) => setStores(data)).catch(() => toast.error('Failed to load accounts'));
    loadPublished();
  }, [loadPublished]);

  useEffect(() => {
    if (!sourceShop) { setCanvases([]); setSelectedId(''); setTruncated(false); return; }
    let cancelled = false;
    setLoadingCv(true);
    // No `shop` param at all = every account. The server caps that list, since
    // a dropdown cannot page.
    api.get('/admin/canvases', { params: sourceShop === ALL_ACCOUNTS ? {} : { shop: sourceShop } })
      .then(({ data }) => {
        if (cancelled) return;
        setCanvases(data.rows || []);
        setTruncated(data.truncated === true);
        setSelectedId('');
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load canvases'); })
      .finally(() => { if (!cancelled) setLoadingCv(false); });
    return () => { cancelled = true; };
  }, [sourceShop]);

  // GET /admin/canvases/published is the only trustworthy "is it published"
  // source: canvas rows carry no isPublished, and publishVisibility survives an
  // unpublish. It is reloaded after every publish and unpublish.
  const publishedRowFor = (id) => (id ? published.find((p) => String(p._id) === String(id)) || null : null);

  const pickCanvas = (id) => {
    setSelectedId(id);
    const row = publishedRowFor(id);
    const targets = currentPublishTargets(row, stores);
    setVisibility(targets.visibility);
    setTargetShops(targets.shops);
    setDescription(row?.templateDescription || '');
    // A pin the pickers can't show (e.g. a model since dropped from MODELS)
    // falls back to Inherit rather than render a blank select.
    const o = row?.execOverrides || {};
    const pair = `${o.provider}|${o.model}`;
    setExecModel(MODELS.some((m) => `${m.provider}|${m.model}` === pair) ? pair : INHERIT);
    setExecEffort(EFFORTS.includes(o.reasoningEffort) ? o.reasoningEffort : INHERIT);
    setExecDelivery(DELIVERIES.includes(o.dataDelivery) ? o.dataDelivery : INHERIT);
  };

  const pickSource = (key) => {
    setSourceShop(key);
    pickCanvas('');
  };

  const current = publishedRowFor(selectedId);
  const currentCount = (current?.publishShops || []).length;

  const canvasLabel = (c) => `${
    // Across accounts, the name alone is ambiguous — two accounts routinely
    // have an "Ads overview".
    sourceShop === ALL_ACCOUNTS ? `${nameOf(c.shop)} — ${c.name}` : c.name
  } · ${c.blockCount} block${c.blockCount === 1 ? '' : 's'}`;

  const publish = async () => {
    if (!selectedId) { toast.error('Pick a canvas to publish'); return; }
    if (visibility === 'stores' && targetShops.length === 0) { toast.error('Select at least one target account'); return; }
    setPublishing(true);
    const tid = toast.loading('Publishing…');
    try {
      await api.post(`/admin/canvases/${selectedId}/publish`, {
        visibility,
        shops: visibility === 'stores' ? targetShops : [],
        description,
        execOverrides: execOverridesPayload(),
      });
      toast.success('Published', { id: tid });
      pickCanvas('');
      await loadPublished();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to publish', { id: tid });
    } finally {
      setPublishing(false);
    }
  };

  const unpublish = async (id) => {
    const tid = toast.loading('Unpublishing…');
    try {
      await api.post(`/admin/canvases/${id}/unpublish`);
      toast.success('Unpublished', { id: tid });
      await loadPublished();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to unpublish', { id: tid });
    }
  };

  return (
    <div style={s.wrap}>
      <div style={s.header}><span style={s.title}>Manage Canvas Templates</span></div>
      <div style={s.body}>
        <div style={s.card}>
          <div style={s.sectionTitle}>Publish a canvas</div>

          <span style={s.label}>Source account</span>
          <div style={s.field}>
            <SearchableSelect
              value={sourceShop}
              options={[
                { value: ALL_ACCOUNTS, label: 'All accounts (global)' },
                ...stores.map((st) => ({ value: st.shopKey, label: st.name, hint: st.shopKey })),
              ]}
              onChange={pickSource}
              styles={{ input: s.trigger }}
              placeholder="Select an account…"
            />
          </div>

          {sourceShop && (
            <>
              <span style={s.label}>Canvas</span>
              <div style={s.field}>
                <SearchableSelect
                  value={selectedId}
                  options={canvases.map((c) => ({ value: c._id, label: canvasLabel(c) }))}
                  onChange={pickCanvas}
                  styles={{ input: s.trigger }}
                  disabled={loadingCv}
                  placeholder={loadingCv ? 'Loading…' : 'Select a canvas…'}
                  emptyText={canvases.length ? 'No matches' : 'No canvases in this account'}
                />
              </div>
              {current && (
                <div style={{ ...s.pubMeta, margin: '-8px 0 14px' }}>
                  Already published to {current.publishVisibility === 'all'
                    ? 'all accounts'
                    : `${currentCount} account${currentCount !== 1 ? 's' : ''}`}. Publishing replaces these
                  targets, the description and the pinned options.
                </div>
              )}
              {truncated && (
                <div style={{ ...s.pubMeta, margin: '-8px 0 14px' }}>
                  Showing the {canvases.length} most recently updated canvases across all accounts.
                  Pick a specific account to see all of its canvases.
                </div>
              )}

              <span style={s.label}>Description (shown on the library card)</span>
              <input style={s.input} value={description} maxLength={500}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Weekly Google Ads performance review" />

              <span style={s.label}>Show it to</span>
              <div style={s.radioRow}>
                <label><input type="radio" name="cvvis" checked={visibility === 'all'} onChange={() => setVisibility('all')} /> All accounts</label>
                <label><input type="radio" name="cvvis" checked={visibility === 'stores'} onChange={() => setVisibility('stores')} /> Specific accounts</label>
              </div>

              {visibility === 'stores' && (
                <div style={s.field}>
                  <SearchableMultiSelect
                    value={targetShops}
                    options={targetStoreOptions(stores, targetShops)}
                    onChange={setTargetShops}
                    styles={{ input: s.trigger }}
                    placeholder="Select accounts…"
                    summarize={(n) => `${n} accounts selected`}
                    selectedFirst
                  />
                </div>
              )}

              <div style={{ margin: '4px 0 14px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Execution options for this template
                </div>
                <p style={{ fontSize: '11.5px', color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
                  Pin how this template&apos;s blocks compute. Leave on Inherit to pin nothing.
                  A pin beats the platform default, but an account or workspace can still override it.
                </p>

                <span style={s.label}>Model</span>
                <select style={s.select} value={execModel} onChange={(e) => setExecModel(e.target.value)}>
                  <option value={INHERIT}>Inherit — whatever the account resolves to</option>
                  {MODELS.map((m) => (
                    <option key={`${m.provider}|${m.model}`} value={`${m.provider}|${m.model}`}>{m.label}</option>
                  ))}
                </select>

                <span style={s.label}>Reasoning effort</span>
                <select style={s.select} value={execEffort} onChange={(e) => setExecEffort(e.target.value)}>
                  <option value={INHERIT}>Inherit — whatever the account resolves to</option>
                  {EFFORTS.map((v) => <option key={v} value={v}>{effortLabel(v)}</option>)}
                </select>

                <span style={s.label}>Data delivery</span>
                <select style={s.select} value={execDelivery} onChange={(e) => setExecDelivery(e.target.value)}>
                  <option value={INHERIT}>Inherit — whatever the account resolves to</option>
                  <option value="auto">Automatic — the planner decides per run</option>
                  <option value="file">Attached file — computed over the full dataset</option>
                  <option value="inline">Inline in the prompt</option>
                </select>

                {(execEffort !== INHERIT || execDelivery !== INHERIT) && (
                  <div style={{ padding: '8px 10px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '11.5px' }}>
                    {COMPUTE_WARNING}
                  </div>
                )}
              </div>

              <button type="button" className="btn-height" style={s.btnPrimary(publishing || !selectedId)} disabled={publishing || !selectedId} onClick={publish}>
                {publishing ? 'Publishing…' : 'Publish'}
              </button>
            </>
          )}
        </div>

        <CanvasExecutionManager stores={stores} />

        <div>
          <div style={s.sectionTitle}>Published canvases</div>
          {published.length === 0 ? (
            <p style={s.empty}>Nothing published yet.</p>
          ) : (
            published.map((p) => (
              <PublishedRow key={p._id} row={p} nameOf={nameOf} onUnpublish={unpublish} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
